import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, EnvelopeSimple, Briefcase, Archive, Eye, Trash, X,
  CheckCircle, Phone, LinkSimple,
} from "@phosphor-icons/react";
import {
  listContactSubmissions, listJobApplications,
  updateContactStatus, updateApplicationStatus,
  deleteContactSubmission, deleteJobApplication,
} from "@/lib/api";
import { Button } from "@/components/ui/button";

const TABS = [
  { key: "contact", label: "Contact",  icon: EnvelopeSimple },
  { key: "careers", label: "Careers",  icon: Briefcase },
];

const STATUS_FILTERS = {
  contact: [
    { key: null, label: "All" },
    { key: "new", label: "New" },
    { key: "read", label: "Read" },
    { key: "archived", label: "Archived" },
  ],
  careers: [
    { key: null, label: "All" },
    { key: "received", label: "Received" },
    { key: "reviewed", label: "Reviewed" },
    { key: "archived", label: "Archived" },
  ],
};

export default function InboxPage() {
  const [tab, setTab] = useState("contact");
  const [statusFilter, setStatusFilter] = useState(null);
  const [selected, setSelected] = useState(null);

  return (
    <div className="min-h-full bg-white text-slate-900" data-testid="admin-inbox-page">
      <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-500 hover:text-brand-blue transition-colors"
          data-testid="inbox-back"
        >
          <ArrowLeft size={12} /> Workspace
        </Link>
        <div className="w-px h-4 bg-border" />
        <h1 className="font-heading font-black text-2xl text-slate-900 tracking-tight">
          Marketing Inbox
        </h1>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          Superadmin
        </div>
      </div>

      <div className="px-6 pt-5 flex items-center gap-3 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setStatusFilter(null); setSelected(null); }}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-t-lg text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-brand-blue text-brand-blue"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`inbox-tab-${t.key}`}
          >
            <t.icon size={14} weight="bold" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS[tab].map((s) => (
          <button
            key={s.label}
            onClick={() => setStatusFilter(s.key)}
            className={`h-8 px-3 rounded-md text-[12px] font-medium transition-colors ${
              statusFilter === s.key
                ? "bg-brand-blue text-white"
                : "bg-white border border-slate-200 text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`inbox-filter-${s.label.toLowerCase()}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr),minmax(0,1.4fr)] h-[calc(100vh-220px)]">
        <InboxList
          kind={tab}
          statusFilter={statusFilter}
          selectedId={selected?.id}
          onSelect={setSelected}
        />
        <InboxDetail item={selected} kind={tab} onClear={() => setSelected(null)} />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */

function InboxList({ kind, statusFilter, selectedId, onSelect }) {
  const { data, isLoading } = useQuery({
    queryKey: ["inbox", kind, statusFilter],
    queryFn: () => (kind === "contact"
      ? listContactSubmissions(statusFilter ? { status: statusFilter } : {})
      : listJobApplications(statusFilter ? { status: statusFilter } : {})),
    refetchInterval: 15000,
  });
  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div className="border-r border-slate-200 overflow-y-auto" data-testid={`inbox-list-${kind}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-500">
        <span>{items.length} shown · {counts.total || 0} total</span>
        {kind === "contact" && counts.new ? (
          <span className="inline-flex items-center gap-1 text-brand-blue font-semibold">
            {counts.new} new
          </span>
        ) : null}
        {kind === "careers" && counts.received ? (
          <span className="inline-flex items-center gap-1 text-brand-blue font-semibold">
            {counts.received} received
          </span>
        ) : null}
      </div>
      {isLoading ? (
        <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-brand-blue/10 flex items-center justify-center text-brand-blue">
            {kind === "contact" ? <EnvelopeSimple size={20} weight="bold" /> : <Briefcase size={20} weight="bold" />}
          </div>
          <div className="mt-4 text-sm font-medium">Nothing here yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Submissions from the public site will show up here.
          </div>
        </div>
      ) : (
        <ul>
          {items.map((it) => (
            <ListRow
              key={it.id}
              item={it}
              kind={kind}
              active={selectedId === it.id}
              onSelect={() => onSelect(it)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ListRow({ item, kind, active, onSelect }) {
  const isUnread = ["new", "received"].includes(item.status);
  const primary = kind === "contact" ? item.subject : item.role;
  const secondary = `${item.name} · ${item.email}`;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`w-full text-left px-4 py-3 border-b border-slate-200/50 flex flex-col gap-1 transition-colors ${
          active ? "bg-brand-blue/10" : "hover:bg-white/[0.02]"
        }`}
        data-testid={`inbox-row-${item.id}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {isUnread ? <span className="w-2 h-2 rounded-full bg-brand-blue shrink-0" /> : null}
            <span className={`truncate text-[13px] ${isUnread ? "font-semibold text-slate-900" : "text-slate-500"}`}>
              {primary}
            </span>
          </div>
          <span className="text-[11px] text-slate-500 font-mono shrink-0">
            {new Date(item.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
          </span>
        </div>
        <div className="text-[12px] text-slate-500 truncate">{secondary}</div>
      </button>
    </li>
  );
}

/* ---------------------------------------------------------------- */

function InboxDetail({ item, kind, onClear }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["inbox"] });

  const markRead = useMutation({
    mutationFn: () =>
      kind === "contact"
        ? updateContactStatus(item.id, "read")
        : updateApplicationStatus(item.id, "reviewed"),
    onSuccess: () => { toast.success("Marked as read"); invalidate(); },
  });
  const archive = useMutation({
    mutationFn: () =>
      kind === "contact"
        ? updateContactStatus(item.id, "archived")
        : updateApplicationStatus(item.id, "archived"),
    onSuccess: () => { toast.success("Archived"); invalidate(); onClear(); },
  });
  const remove = useMutation({
    mutationFn: () =>
      kind === "contact"
        ? deleteContactSubmission(item.id)
        : deleteJobApplication(item.id),
    onSuccess: () => { toast.success("Deleted"); invalidate(); onClear(); },
  });

  if (!item) {
    return (
      <div className="hidden lg:flex flex-col items-center justify-center text-center px-10">
        <div className="w-16 h-16 rounded-2xl bg-brand-blue/10 flex items-center justify-center text-brand-blue">
          <EnvelopeSimple size={26} weight="bold" />
        </div>
        <div className="mt-5 font-heading font-bold text-lg">Select a submission</div>
        <p className="text-sm text-slate-500 max-w-xs mt-2">
          Click any entry on the left to view its full body and manage its status.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden" data-testid={`inbox-detail-${item.id}`}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            {kind === "contact" ? "Contact submission" : "Job application"} · {item.status}
          </div>
          <div className="mt-1 font-heading font-bold text-lg text-slate-900 truncate">
            {kind === "contact" ? item.subject : item.role}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!(item.status === "read" || item.status === "reviewed") ? (
            <Button size="sm" variant="outline" onClick={() => markRead.mutate()} data-testid="inbox-mark-read">
              <Eye size={14} className="mr-1" /> Mark read
            </Button>
          ) : null}
          {item.status !== "archived" ? (
            <Button size="sm" variant="outline" onClick={() => archive.mutate()} data-testid="inbox-archive">
              <Archive size={14} className="mr-1" /> Archive
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              if (confirm("Delete permanently?")) remove.mutate();
            }}
            data-testid="inbox-delete"
          >
            <Trash size={14} className="mr-1" /> Delete
          </Button>
          <button
            onClick={onClear}
            className="w-8 h-8 rounded-md text-slate-500 hover:bg-white/5 flex items-center justify-center lg:hidden"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="p-6 overflow-y-auto space-y-6">
        <DetailMeta item={item} kind={kind} />
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
            {kind === "contact" ? "Message" : "Cover letter"}
          </div>
          <div className="p-5 rounded-lg bg-white border border-slate-200 whitespace-pre-wrap text-[14px] leading-relaxed">
            {kind === "contact" ? item.message : item.cover_letter}
          </div>
        </div>
        <EmailStatus item={item} />
      </div>
    </div>
  );
}

function DetailMeta({ item, kind }) {
  const rows = useMemo(() => {
    if (kind === "contact") {
      return [
        ["Name", item.name],
        ["Email", item.email],
        ["Company", item.company || "—"],
        ["Phone", item.phone || "—"],
        ["Source", item.source || "—"],
        ["Received", new Date(item.created_at).toLocaleString()],
      ];
    }
    return [
      ["Role", item.role],
      ["Name", item.name],
      ["Email", item.email],
      ["Phone", item.phone || "—"],
      ["Experience", item.experience_years != null ? `${item.experience_years} yrs` : "—"],
      ["Resume", item.resume_url || "—"],
      ["Received", new Date(item.created_at).toLocaleString()],
    ];
  }, [item, kind]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 border border-slate-200 rounded-lg p-5">
      {rows.map(([k, v]) => (
        <div key={k} className="text-[13px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
          <div className="mt-0.5 text-slate-900 break-words">
            {String(v).startsWith("http") ? (
              <a href={v} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline inline-flex items-center gap-1">
                <LinkSimple size={12} /> {v}
              </a>
            ) : k === "Email" ? (
              <a href={`mailto:${v}`} className="text-brand-blue hover:underline">{v}</a>
            ) : k === "Phone" && v !== "—" ? (
              <a href={`tel:${String(v).replace(/\s+/g, "")}`} className="text-brand-blue hover:underline inline-flex items-center gap-1">
                <Phone size={12} /> {v}
              </a>
            ) : (
              v
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmailStatus({ item }) {
  const es = item.email_status || {};
  if (es.delivered) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-emerald-400" data-testid="inbox-email-status">
        <CheckCircle size={14} weight="fill" />
        Email delivered · id {es.id}
      </div>
    );
  }
  return (
    <div className="text-[12px] text-slate-500" data-testid="inbox-email-status">
      Email not delivered — {es.reason || "no email provider configured"}. Reply directly from your mail client.
    </div>
  );
}
